// Pure message-handler for the widget sign bridge. Lifted out of the
// useEffect wrapper so the trust-model branches (source pinning,
// wallet-error responses, ignored message kinds) are unit-testable
// without mounting a React tree or dispatching real DOM events.

import type { Signer } from '@cef-ai/signer';
import { bytesToHex } from '@/lib/encoding/hex';
import {
  WIDGET_IDENTITY_REQUEST_TYPE,
  WIDGET_IDENTITY_RESPONSE_TYPE,
  WIDGET_SIGN_REQUEST_TYPE,
  WIDGET_SIGN_RESPONSE_TYPE,
  arrayToBytes,
  bytesToArray,
  isWidgetBridgeRequest,
  type WidgetIdentityResponse,
  type WidgetSignResponse,
} from './protocol';

/**
 * Reply the bridge sends when the user declines (or hasn't granted) the
 * one-time consent to let widgets sign with the connected wallet.
 * Exported so the WidgetIframeModal copy and any widget-author docs can
 * share the exact wording — keeps the contract single-sourced.
 */
export const WIDGET_SIGN_DENIED_ERROR =
  'widget signing was declined — approve "Allow widgets to sign" in the vault '
  + 'to enable it, or use the identity bridge / published handles for non-signing operations';

export type WidgetBridgeReply = WidgetIdentityResponse | WidgetSignResponse;

export interface MinimalMessageEvent {
  data: unknown;
  source: unknown;
}

export interface HandleArgs {
  signer: Signer | null;
  /**
   * Only messages whose `event.source` is reference-equal to this value
   * are answered. Pass the active widget iframe's `contentWindow` so a
   * second, unrelated frame on the page (or a postMessage from
   * `window.opener`) is silently dropped. The vault embeds third-party
   * widget HTML, so any other behavior would hand the user's wallet to
   * arbitrary code.
   */
  allowedSource: unknown;
  /**
   * Resolves the user's consent to sign widget-supplied bytes. Called
   * only after the trust gates (wallet present, source pinned) pass, and
   * only for sign requests. Implementations may short-circuit to `true`
   * when a remembered grant exists, or open a consent dialog otherwise.
   * The wallet signs raw bytes with no native prompt, so this is the only
   * user gesture protecting the signing surface.
   */
  ensureSignConsent: () => Promise<boolean>;
  reply: (msg: WidgetBridgeReply) => void;
}

export async function handleWidgetMessage(
  event: MinimalMessageEvent,
  args: HandleArgs,
): Promise<void> {
  if (!args.signer) return;
  if (!isWidgetBridgeRequest(event.data)) return;
  if (event.source !== args.allowedSource) return;

  const data = event.data;

  if (data.type === WIDGET_IDENTITY_REQUEST_TYPE) {
    try {
      const sigType = args.signer.type;
      if (sigType !== 'ed25519' && sigType !== 'sr25519') {
        args.reply({
          type: WIDGET_IDENTITY_RESPONSE_TYPE,
          requestId: data.requestId,
          pubkey: '',
          error: `widget identity bridge does not support signer type "${sigType}"`,
        });
        return;
      }
      args.reply({
        type: WIDGET_IDENTITY_RESPONSE_TYPE,
        requestId: data.requestId,
        pubkey: `0x${bytesToHex(args.signer.publicKey)}`,
        sigType,
      });
    } catch (e) {
      args.reply({
        type: WIDGET_IDENTITY_RESPONSE_TYPE,
        requestId: data.requestId,
        pubkey: '',
        error: e instanceof Error ? e.message : String(e),
      });
    }
    return;
  }

  if (data.type === WIDGET_SIGN_REQUEST_TYPE) {
    // Gate every sign on the consent requester. `ed25519_signRaw` is
    // authorised once at wallet connect with no per-signature prompt, so
    // without this gate any installed widget could mint delegation
    // tokens, sign extrinsics, or impersonate the user. The 64KB cap is
    // a DoS guard, not a security boundary. `ensureSignConsent` is a
    // caller-supplied function — this is the one choke point every current
    // and future implementation passes through before `signer.sign`, so we
    // treat a throw/rejection the same as an outright refusal: a
    // deterministic denial reply. Without this, a throwing implementation
    // would escape as an unhandled rejection under attach.ts's
    // `void handleWidgetMessage(...)`, leaving the widget with no reply at
    // all instead of the clear UX failure it's promised. Crucially we
    // never reach `signer.sign` on any of these paths.
    let granted: boolean;
    try {
      granted = await args.ensureSignConsent();
    } catch {
      args.reply({
        type: WIDGET_SIGN_RESPONSE_TYPE,
        requestId: data.requestId,
        signature: null,
        error: WIDGET_SIGN_DENIED_ERROR,
      });
      return;
    }
    if (!granted) {
      args.reply({
        type: WIDGET_SIGN_RESPONSE_TYPE,
        requestId: data.requestId,
        signature: null,
        error: WIDGET_SIGN_DENIED_ERROR,
      });
      return;
    }
    // signer.sign may take time (and the modal may close mid-flight); the
    // stale-reply race is handled by attach.ts pinning the reply origin
    // and the widget treating a missing response as a denial.
    try {
      const sig = await args.signer.sign(arrayToBytes(data.bytes), 'data');
      args.reply({
        type: WIDGET_SIGN_RESPONSE_TYPE,
        requestId: data.requestId,
        signature: bytesToArray(sig),
      });
    } catch (e) {
      args.reply({
        type: WIDGET_SIGN_RESPONSE_TYPE,
        requestId: data.requestId,
        signature: null,
        error: e instanceof Error ? e.message : String(e),
      });
    }
  }
}
